import { AnyAction } from 'redux';
import { RootState } from '../types';
import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { ThunkDispatch } from 'redux-thunk';
import { translate } from '../../i18n/translate';

interface SendFeedbackParams {
  FeedbackContent: string;
}

interface FeedbackEntry {
  id: number;
  feedback: string;
  created_at: string;
}

interface FeedbackListResponse {
  success: boolean;
  feedback_list: FeedbackEntry[];
  total: number;
  limit: number;
  offset: number;
}

export const sendFeedback = ({ FeedbackContent }: SendFeedbackParams) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'POST',
        url: '/feedback/send',
        data: { feedback: FeedbackContent },
      });

      if (response.data && response.data.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Feedback sent successfully'),
          modalType: 'success',
          duration: 3000
        }));
        return response.data;
      } else {
        const errorMessage = response.data?.message || 'Failed to send feedback';
        dispatch(showFeedbackModal({
          showModal: true,
          message: errorMessage,
          modalType: 'error',
          duration: 3000
        }));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send feedback';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000
      }));
      throw error;
    }
  };

export const fetchUserFeedback = (limit: number = 50, offset: number = 0) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'GET',
        url: '/feedback/my-feedback',
        params: { limit, offset },
      });

      if (response.data && response.data.success) {
        return response.data as FeedbackListResponse;
      } else {
        throw new Error(response.data?.message || 'Failed to fetch feedback');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch feedback';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000
      }));
      throw error;
    }
  };

export const deleteFeedback = (feedbackId: number) =>
  async (dispatch: ThunkDispatch<RootState, undefined, AnyAction>) => {
    try {
      const response = await apis({
        method: 'DELETE',
        url: `/feedback/${feedbackId}`,
      });

      if (response.data && response.data.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: translate('Feedback deleted successfully'),
          modalType: 'success',
          duration: 2000
        }));
        return response.data;
      } else {
        throw new Error(response.data?.message || 'Failed to delete feedback');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete feedback';
      dispatch(showFeedbackModal({
        showModal: true,
        message: errorMessage,
        modalType: 'error',
        duration: 3000
      }));
      throw error;
    }
  };
